// src/web/client/src/utils/shopifyAuth.js
//
// SINGLE source of truth for the Shopify session token.
//
// THE PRINCIPLE (set after the "all-expired" incident): the LIVE source
// always goes first and always wins; the static leftovers (URL param,
// stored value) are emergency fallbacks only — and an old value must NEVER
// overwrite or outrank a fresh one. Session tokens live 60 seconds: in an
// SPA the address bar never changes after entry, so the URL id_token is
// the ENTRY token forever — after the first minute it is expired BY
// CONSTRUCTION. Any ordering that lets it win sends garbage.
//
// Priority:
//   1. window.shopify.idToken() — App Bridge v4, LIVE. Called FRESH on
//      every request (never memoized — pinned by test). We also WAIT for
//      it to appear within the time budget: on a cold boot the API calls
//      can fire before App Bridge finished attaching idToken, and an
//      instant "not there → fallback" would pick a stale source even
//      though the live one was a moment away.
//   2/3. URL id_token + stored value — emergency fallbacks, kept on
//      purpose (non-embedded contexts, future regressions). BUT: each
//      candidate's own exp claim is checked first, an expired candidate
//      is skipped entirely (an honest `missing` beats sending a token
//      that is known-dead), the FRESHER of the two wins, and persisting
//      never downgrades a newer stored token to an older URL one.

// Total time budget for the live source: waiting for App Bridge to attach
// idToken AND the idToken() call itself share this window. 3s is generous
// for a healthy handshake (sub-second in practice) and short enough that a
// pathological pend never reads as a frozen app (the #97 lesson).
const APP_BRIDGE_TOKEN_TIMEOUT_MS = 3000;
const AVAILABILITY_POLL_MS = 100;

// The last resort, used ONLY when App Bridge is attached but idToken() has not
// answered within the budget AND no usable fallback exists. At that point the
// alternative is to send the request with no token at all — a request that is
// GUARANTEED to come back 401 (B2 enforcement), which the screens then read as
// "your session expired" (seen on the brain screens, 2026-10-26: a 3s wait, a
// tokenless request, a 401, one retry of the same, and an error card). Waiting
// longer for the live source can only end better: a token, or the same honest
// `missing`, 9s later. Still a hard cap, so a pathological pend never freezes
// the app (the #97 lesson), and the standalone / non-embedded path — no App
// Bridge at all — is untouched: it falls back after the 3s budget as before.
const APP_BRIDGE_LAST_RESORT_MS = 12000;

// Small safety margin when judging a candidate's exp claim, so a token that
// is about to die mid-flight isn't sent.
const EXP_SKEW_MS = 5000;

// ── JWT exp reading (NO verification — that's the backend's job; we only
//    read the public exp claim to avoid knowingly sending a dead token). ──
export function tokenExpiryMs(token) {
  try {
    const payload = token.split('.')[1];
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64));
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(token) {
  if (!token) return false;
  const exp = tokenExpiryMs(token);
  // A token whose exp we can't read is passed through — the backend is the
  // real judge; we only refuse what is PROVABLY dead.
  return exp === null || exp > Date.now() + EXP_SKEW_MS;
}

// Persist WITHOUT downgrading: never overwrite a fresher stored token with
// an older one (the second ordering bug — the stale URL token used to
// clobber a fresh token a previous live call had just saved).
function persistIfFresher(token) {
  const current = localStorage.getItem('shopify_session_token');
  if (current) {
    const curExp = tokenExpiryMs(current);
    const newExp = tokenExpiryMs(token);
    if (curExp !== null && newExp !== null && newExp <= curExp) return;
  }
  localStorage.setItem('shopify_session_token', token);
}

// Save the entry token at module load (initial embedded load) — it IS fresh
// at that moment; persistIfFresher keeps it from clobbering anything newer.
const entryToken = new URLSearchParams(window.location.search).get('id_token');
if (entryToken) {
  persistIfFresher(entryToken);
  console.log('[ShopifyAuth] Saved entry id_token');
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Whether any emergency fallback (URL id_token / stored token) is still usable.
function usableFallback() {
  const urlToken = new URLSearchParams(window.location.search).get('id_token');
  const storedToken = localStorage.getItem('shopify_session_token');
  return [urlToken, storedToken].some(isUsable);
}

export const getShopifySessionToken = async () => {
  const start = Date.now();
  const deadline = start + APP_BRIDGE_TOKEN_TIMEOUT_MS;

  // ── 1. THE LIVE SOURCE — always first, always wins when it answers. ──
  try {
    if (typeof window !== 'undefined') {
      // Wait (within the budget) for App Bridge to attach idToken — a cold
      // boot must not fall back to stale sources just because the first API
      // call raced the App Bridge script.
      while (!window.shopify?.idToken && Date.now() < deadline) {
        await sleep(AVAILABILITY_POLL_MS);
      }
      if (window.shopify?.idToken) {
        const remaining = Math.max(deadline - Date.now(), 250);
        // Fresh call EVERY time — never cached (session tokens die in 60s).
        // One call, awaited twice at most: the budget race below, and — only
        // when nothing usable exists to fall back on — the last-resort race.
        const live = Promise.resolve()
          .then(() => window.shopify.idToken())
          .catch((err) => { console.warn('[ShopifyAuth] App Bridge idToken failed:', err?.message); return null; });
        const fresh = await Promise.race([
          live,
          new Promise((resolve) => setTimeout(() => resolve(null), remaining)),
        ]);
        if (fresh) {
          persistIfFresher(fresh);
          return fresh;
        }
        console.warn('[ShopifyAuth] App Bridge idToken did not answer in time — falling back');
        if (!usableFallback()) {
          // Nothing to fall back on: a request now would certainly 401. Keep
          // waiting for the live source, up to the hard cap.
          const lastResort = Math.max(start + APP_BRIDGE_LAST_RESORT_MS - Date.now(), 0);
          const late = await Promise.race([
            live,
            new Promise((resolve) => setTimeout(() => resolve(null), lastResort)),
          ]);
          if (late) {
            persistIfFresher(late);
            return late;
          }
          console.warn('[ShopifyAuth] App Bridge idToken never answered — no token');
        }
      } else {
        console.warn('[ShopifyAuth] App Bridge idToken not available — falling back');
      }
    }
  } catch (err) {
    console.warn('[ShopifyAuth] App Bridge idToken failed, falling back:', err?.message);
  }

  // ── 2/3. EMERGENCY FALLBACKS — freshest USABLE candidate wins;
  //         provably-expired candidates are skipped entirely. ──
  const urlToken = new URLSearchParams(window.location.search).get('id_token');
  const storedToken = localStorage.getItem('shopify_session_token');

  const candidates = [urlToken, storedToken].filter(isUsable);
  if (candidates.length === 0) {
    // Honest `missing`: sending nothing beats sending a token we KNOW is
    // dead. The backend (B1: never blocks) serves the request either way,
    // and the metric log tells the truth.
    console.warn('[ShopifyAuth] No usable session token available');
    return null;
  }

  candidates.sort((a, b) => (tokenExpiryMs(b) ?? Infinity) - (tokenExpiryMs(a) ?? Infinity));
  const best = candidates[0];
  persistIfFresher(best);
  return best;
};

// Save token when we have it
export const saveSessionToken = () => {
  const idToken = new URLSearchParams(window.location.search).get('id_token');
  if (idToken) {
    persistIfFresher(idToken);
    console.log('[ShopifyAuth] Token saved');
  }
};

// Legacy function - kept for compatibility
export const setAppBridgeInstance = (app) => {
  // Do nothing
};
